import type { ViewHook } from '../../helpers/utils'
import {
  MARKDOC_EDITOR_HOVER_EVENT,
  MARKDOC_PREVIEW_HOVER_EVENT,
  type MarkdocSourceHoverDetail,
  type MarkdocSourceLineRange,
  normalizeSourceLineRange,
  readSourceHoverDetail
} from '../markdoc_plugin/source-hover'

interface MarkdocPreviewHook extends ViewHook {
  _cleanup: (() => void) | null
  _hoveredElement: HTMLElement | null
}

const SOURCE_RANGE_SELECTOR = '[data-markdoc-source-range]'
const SOURCE_HOVER_CLASS = 'markdoc-preview-source-hover'

const readElementSourceRange = (
  element: HTMLElement
): MarkdocSourceLineRange | null =>
  normalizeSourceLineRange(
    Number(element.dataset.markdocSourceFromLine),
    Number(element.dataset.markdocSourceToLine)
  )

const isElementTarget = (target: EventTarget | null): target is Element =>
  typeof target === 'object' &&
  target !== null &&
  'closest' in target &&
  typeof target.closest === 'function'

const sourceElementAtTarget = (
  root: HTMLElement,
  target: EventTarget | null
): HTMLElement | null => {
  const element = isElementTarget(target) ? target : null
  const sourceElement =
    element?.closest<HTMLElement>(SOURCE_RANGE_SELECTOR) ?? null

  return sourceElement !== null && root.contains(sourceElement)
    ? sourceElement
    : null
}

const sourceElementForLine = (
  root: HTMLElement,
  line: number
): HTMLElement | null =>
  Array.from(root.querySelectorAll<HTMLElement>(SOURCE_RANGE_SELECTOR))
    .map((element) => ({ element, range: readElementSourceRange(element) }))
    .filter(
      (
        entry
      ): entry is {
        readonly element: HTMLElement
        readonly range: MarkdocSourceLineRange
      } =>
        entry.range !== null &&
        entry.range.fromLine <= line &&
        line <= entry.range.toLine
    )
    .sort(
      (left, right) =>
        left.range.toLine -
        left.range.fromLine -
        (right.range.toLine - right.range.fromLine)
    )[0]?.element ?? null

const dispatchPreviewHover = (detail: MarkdocSourceHoverDetail): void => {
  document.dispatchEvent(
    new CustomEvent<MarkdocSourceHoverDetail>(MARKDOC_PREVIEW_HOVER_EVENT, {
      detail
    })
  )
}

const setHoveredElement = (
  hook: MarkdocPreviewHook,
  element: HTMLElement | null
): void => {
  if (hook._hoveredElement === element) {
    return
  }

  hook._hoveredElement?.classList.remove(SOURCE_HOVER_CLASS)
  element?.classList.add(SOURCE_HOVER_CLASS)
  hook._hoveredElement = element
}

export const MarkdocPreview = {
  mounted(this: MarkdocPreviewHook) {
    this._hoveredElement = null

    const handlePointerOver = (event: PointerEvent): void => {
      const sourceElement = sourceElementAtTarget(this.el, event.target)
      setHoveredElement(this, sourceElement)

      const range =
        sourceElement === null ? null : readElementSourceRange(sourceElement)

      dispatchPreviewHover(
        range === null ? { type: 'clear' } : { type: 'range', range }
      )
    }

    const handlePointerLeave = (): void => {
      setHoveredElement(this, null)
      dispatchPreviewHover({ type: 'clear' })
    }

    const handleEditorHover = (event: Event): void => {
      if (!(event instanceof CustomEvent)) {
        return
      }

      const detail = readSourceHoverDetail(event.detail)

      if (detail === null || detail.type === 'clear') {
        setHoveredElement(this, null)
        return
      }

      setHoveredElement(
        this,
        sourceElementForLine(this.el, detail.range.fromLine)
      )
    }

    this.el.addEventListener('pointerover', handlePointerOver)
    this.el.addEventListener('pointerleave', handlePointerLeave)
    document.addEventListener(MARKDOC_EDITOR_HOVER_EVENT, handleEditorHover)

    this._cleanup = () => {
      this.el.removeEventListener('pointerover', handlePointerOver)
      this.el.removeEventListener('pointerleave', handlePointerLeave)
      document.removeEventListener(
        MARKDOC_EDITOR_HOVER_EVENT,
        handleEditorHover
      )
      setHoveredElement(this, null)
      dispatchPreviewHover({ type: 'clear' })
    }
  },

  updated(this: MarkdocPreviewHook) {
    setHoveredElement(this, null)
    dispatchPreviewHover({ type: 'clear' })
  },

  destroyed(this: MarkdocPreviewHook) {
    this._cleanup?.()
    this._cleanup = null
  }
}

export { readElementSourceRange, sourceElementAtTarget, sourceElementForLine }
